import { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
} from 'reactstrap';
import { FaSearch, FaUser, FaRegBuilding, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { formatPrice } from '../../helpers/utility';
import { supplierActions } from '../../sagas/supplierSlice';

const EtatFournisseur = () => {

    // États
    const { suppliers, loading: suppliersLoading, totalPages: suppliersTotalPages, totalElements: suppliersTotalElements } = useSelector(state => state.suppliers);
    const [filteredFournisseurs, setFournisseurs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [balanceFilter, setBalanceFilter] = useState('ALL'); // 'ALL' | 'DEBITEUR' | 'CREDITEUR'


    useEffect(() => {
        dispatch(supplierActions.findAll({
            query: ""
        }));
    }, []);

    // Filtrage et recherche
    useEffect(() => {
        if (suppliersLoading) return;

        let result = suppliers;

        // Recherche
        if (searchTerm) {
            result = result.filter(sup => {
                const supName = sup.typeFournisseur === 'PARTICULIER'
                    ? `${sup.nom} ${sup.prenom}`
                    : sup.entreprise;
                return supName.toLowerCase().includes(searchTerm.toLowerCase())
                    || sup.code.toString().includes(searchTerm);
            });
        }

        // 🔎 Filtre Débiteur / Créditeur / Tous
        if (balanceFilter === 'DEBITEUR') {
            // Débiteur = solde > 0 (client doit payer)
            result = result.filter(c => Number(c.solde) > 0);
        } else if (balanceFilter === 'CREDITEUR') {
            // Créditeur = solde < 0 (vous devez au client)
            result = result.filter(c => Number(c.solde) < 0);
        }
        // 'ALL' => aucun filtre supplémentaire

        setFournisseurs(result);
        setCurrentPage(1);
    }, [searchTerm, suppliers, balanceFilter, suppliersLoading]);

    return (
        <div>
            {/* Header Section */}

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUsers className="text-primary me-2" />
                                {t('supplier.etatSupplier')}
                            </h4>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {suppliersLoading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom, email, téléphone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-2">
                                <Button
                                    color={balanceFilter === 'ALL' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => setBalanceFilter('ALL')}
                                    outline={balanceFilter !== 'ALL'}
                                >
                                    Tous
                                </Button>
                                <Button
                                    color={balanceFilter === 'DEBITEUR' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => setBalanceFilter('DEBITEUR')}
                                    outline={balanceFilter !== 'DEBITEUR'}
                                    title="Fournisseurs débiteurs (solde > 0)"
                                >
                                    Débiteur
                                </Button>
                                <Button
                                    color={balanceFilter === 'CREDITEUR' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => setBalanceFilter('CREDITEUR')}
                                    outline={balanceFilter !== 'CREDITEUR'}
                                    title="Fournisseurs créditeurs (solde < 0)"
                                >
                                    Créditeur
                                </Button>
                            </div>
                            <table className="table table-hover table-striped table-bordered mt-2" >
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">{t('client.nom')}</th>
                                        <th className="align-middle text-center">{t('client.contact')}</th>
                                        <th className="align-middle text-center">{t('client.totalDebit')}</th>
                                        <th className="align-middle text-center">{t('client.totalCredit')}</th>
                                        <th className="align-middle text-center">{t('client.solde')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredFournisseurs.map(sup => (
                                        <tr key={sup.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {sup.typeFournisseur === 'PARTICULIER' ? (
                                                            <FaUser className="text-primary" />
                                                        ) : (
                                                            <FaRegBuilding className="text-info" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">
                                                            {sup.typeFournisseur === 'PARTICULIER'
                                                                ? `${sup.nom} ${sup.prenom}`
                                                                : sup.entreprise}
                                                        </div>
                                                        <small className="text-muted">
                                                            {sup.typeFournisseur === 'PARTICULIER' ? 'Particulier' : 'Entreprise'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">

                                                    <div>
                                                        <div className="fw-bold">
                                                            {sup.telephone1}
                                                        </div>
                                                        <small className="text-muted">
                                                            {sup.telephone2 || sup.email || '-'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(sup.totalDebit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(sup.totalCredit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                {Number(sup.solde) < 0 ? (
                                                    <span className="text-success fw-bold">+{formatPrice(Math.abs(sup.solde))}</span>
                                                ) : Number(sup.solde) > 0 ? (
                                                    <span className="text-danger fw-bold">-{formatPrice(Math.abs(sup.solde))}</span>
                                                ) : (
                                                    <span className="text-dark fw-bold">{formatPrice(0)}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}

                                    {filteredFournisseurs.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                <div className="text-muted">{t('delivery.noBL')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light fw-bold">
                                        <td colSpan="2" className="text-end">
                                            {t('common.totals')}
                                        </td>

                                        {/* Total Débit */}
                                        <td className="text-danger">
                                            {formatPrice(
                                                filteredFournisseurs.reduce((sum, c) => sum + Number(c.totalDebit || 0), 0)
                                            )}
                                        </td>

                                        {/* Total Crédit */}
                                        <td className="text-success">
                                            {formatPrice(
                                                filteredFournisseurs.reduce((sum, c) => sum + Number(c.totalCredit || 0), 0)
                                            )}
                                        </td>

                                        {/* Solde global */}
                                        <td>
                                            {(() => {
                                                const totalDebit = filteredFournisseurs.reduce(
                                                    (sum, c) => sum + Number(c.totalDebit || 0),
                                                    0
                                                );
                                                const totalCredit = filteredFournisseurs.reduce(
                                                    (sum, c) => sum + Number(c.totalCredit || 0),
                                                    0
                                                );
                                                const solde = totalDebit - totalCredit;

                                                if (solde < 0)
                                                    return (
                                                        <span className="text-success">
                                                            +{formatPrice(Math.abs(solde))}
                                                        </span>
                                                    );
                                                if (solde > 0)
                                                    return (
                                                        <span className="text-danger">
                                                            -{formatPrice(Math.abs(solde))}
                                                        </span>
                                                    );
                                                return <span className="text-dark">{formatPrice(0)}</span>;
                                            })()}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default EtatFournisseur;